import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, guard } from '../services/auth.api'
import { HealthcareParty, HealthcarePartyFilters, Pkcs8Bytes } from '@icure/cardinal-sdk'
import { loadFromIterator, tagsByIds } from './utils'

export const practitionerApiRtk = createApi({
  reducerPath: 'practitionerApi',
  tagTypes: ['Practitioner'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    getPractitioner: builder.query<HealthcareParty | undefined, string>({
      async queryFn(id, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        return guard([practitionerApi], async ([practitionerApi]): Promise<HealthcareParty> => {
          const practitioner = await practitionerApi.getHealthcareParty(id)

          if (!practitioner) {
            throw new Error('Practitioner does not exist')
          }
          return practitioner
        })
      },
      providesTags: (res) => (res ? [{ type: 'Practitioner', id: res.id }] : []),
    }),
    getPractitioners: builder.query<HealthcareParty[] | undefined, string[]>({
      async queryFn(ids, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        return guard([practitionerApi], async ([practitionerApi]): Promise<Array<HealthcareParty>> => {
          const practitioners = await practitionerApi.getHealthcareParties(ids)
          if (!practitioners) {
            throw new Error('Error while getting practitioners')
          }
          return practitioners
        })
      },
      providesTags: (res) =>
        res
          ? [
              { type: 'Practitioner', id: 'all' },
              ...res.map((practitioner) => {
                return {
                  type: 'Practitioner',
                  id: practitioner.id,
                } as { type: 'Practitioner'; id: string }
              }),
            ]
          : [],
    }),
    listHealthcarePartiesByParentId: builder.query<HealthcareParty[] | undefined, string>({
      async queryFn(parentId, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        return guard([practitionerApi], async ([practitionerApi]): Promise<Array<HealthcareParty>> => {
          const practitioners = await loadFromIterator(await practitionerApi.filterHealthPartiesBy(HealthcarePartyFilters.byParentId(parentId)), 1000)
          if (!practitioners) {
            throw new Error('Error while getting practitioners')
          }
          return practitioners
        })
      },
      providesTags: (res) =>
        res
          ? [
              { type: 'Practitioner', id: 'all' },
              ...res.map((practitioner) => {
                return {
                  type: 'Practitioner',
                  id: practitioner.id,
                } as { type: 'Practitioner'; id: string }
              }),
            ]
          : [],
    }),
    getOwnPrivateKeys: builder.query<Record<string, Pkcs8Bytes> | undefined, void>({
      async queryFn(_, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        const cryptoApi = (await cardinalApi(getState))?.crypto
        return guard([practitionerApi, cryptoApi], async ([practitionerApi, cryptoApi]): Promise<Record<string, Pkcs8Bytes>> => {
          const keys = (await cryptoApi.currentDataOwnerKeys())[(await practitionerApi.getCurrentHealthcareParty()).id]
          if (!keys) {
            throw new Error('Practitioner has no keys')
          }
          return keys
        })
      },
      providesTags: () => [],
    }),
    filterPractitionersByName: builder.query<HealthcareParty[] | undefined, string>({
      async queryFn(name, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        return guard([practitionerApi], async ([practitionerApi]): Promise<HealthcareParty[]> => {
          return await loadFromIterator(await practitionerApi.filterHealthPartiesBy(HealthcarePartyFilters.byName(name)), 1000)
        })
      },
      providesTags: tagsByIds('Practitioner'),
    }),
    createOrUpdatePractitioner: builder.mutation<HealthcareParty | undefined, HealthcareParty>({
      async queryFn(practitioner, { getState }) {
        const practitionerApi = (await cardinalApi(getState))?.healthcareParty
        return guard([practitionerApi], async ([practitionerApi]): Promise<HealthcareParty> => {
          const updatedPractitioner = !!practitioner.rev ? await practitionerApi.modifyHealthcareParty(practitioner) : await practitionerApi.createHealthcareParty(practitioner)
          if (!updatedPractitioner) {
            throw new Error('Practitioner does not exist')
          }
          return updatedPractitioner
        })
      },
      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Practitioner', id: 'all' },
              { type: 'Practitioner', id: result.id },
            ]
          : [],
    }),
  }),
})

export const {
  useGetPractitionerQuery,
  useGetPractitionersQuery,
  useGetOwnPrivateKeysQuery,
  useFilterPractitionersByNameQuery,
  useCreateOrUpdatePractitionerMutation,
  useListHealthcarePartiesByParentIdQuery,
} = practitionerApiRtk
